import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  LOADING_STATUS,
  LOCALES,
  ORDER_DIRECTION,
} from '../../common/constants';
import searchService, {
  AccommodationPublicDetails,
  AccommodationPublicDetailsResponse,
} from '../../common/api/searchService';
import { RootState } from '../../app/store';
import {
  InitialPaginationParameters,
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';
import {
  fetchDefaultRateDetails,
  resetSearchResults,
} from '../../common/api/defaultRatesService';
import { AutoSuggestOption } from '../autocomplete/autocompleteSlice';

export enum ORDER_BY {
  Random = 'Random',
  Price = 'Price',
  AccommodationNumber = 'AccommodationNumber',
}

export interface LastMinuteSearchResultRuleData {
  percentage?: number;
  difference?: number;
  daysUntilStart: number;
}

export interface CustomerFeedbackSearchResultData {
  feedbackCount?: number;
  averageAnswerValue?: number;
}

export interface NearbyLocationData {
  targetLocationName?: string;
  distance?: string;
}

export interface SearchResultItem {
  id: string;
  accommodationNumber: number;
  name: string;
  price: number;
  maxPersons?: number;
  area?: number;
  bedrooms?: number;
  beds: number;
  extraBeds: number;
  defaultPreparationDays?: number;
  distanceToSkiResortInKilometers?: number;
  originalPriceForTravelAgency: number;
  ownerNumber: string;
  ownerName: string;
  regionName?: string;
  skiResortName?: string;
  municipalityName?: string;
  city?: string;
  accommodationSlug?: string;
  isBlockedByAvailabilityRules: boolean;
  minimumWeekPrice: number;
  minimumOneDayPrice: number;
  lastMinuteRuleData?: LastMinuteSearchResultRuleData;
  customerFeedbackData?: CustomerFeedbackSearchResultData;
  nearbyLocationData?: NearbyLocationData;
  includedAddonNames: string[];
  mainImageUrl?: string;
  mainImageThumbUrl?: string;
  imageUrls?: string[];
  blockingMessages?: string[];
  priceCalculationLogs?: string[];
}

export interface MultiSelectAttribute {
  attributeId?: string;
  optionId?: string;
}

export interface SearchBookableAccommodationsFilter
  extends PaginationParameters {
  includeBlockedByAvailabilityRules: boolean;
  useOnSaleOnly?: boolean;
  autosuggestions?: AutoSuggestOption[];
  searchString: string;
  accommodationId: string;
  arrivalDateTime?: string;
  lengthOfStay?: number;
  maxPersonCount?: string;
  numberOfBedrooms?: string;
  numberOfToilets?: string;
  qualityRatings: [];
  interhomeService?: boolean;
  area?: number;
  maxPrice?: number;
  nearbySearchRadius?: number;
  nearbySearchLimit?: number;
  nearbyAccommodationsCount?: number;
  minimumNumericAttributes?: Record<string, number>;
  maximumNumericAttributes?: Record<string, number>;
  booleanAttributes?: string[];
  multiSelectAttributes?: MultiSelectAttribute[];
  textAttributes?: Record<string, string>;
  cultureOption: LOCALES;
  orderBy: ORDER_BY;
  orderDirection: ORDER_DIRECTION;
  logPriceCalculation?: boolean;
  skipAvailabilityCheck?: boolean;
}

export interface AccommodationPublicDetailsParams {
  cottage: string;
  siteId: string;
  includeBasicData?: boolean;
  includeLocation?: boolean;
  includeImages?: boolean;
  includeAttributes?: boolean;
  includeDistances?: boolean;
  includeAddOns?: boolean;
  locale?: string;
}
interface SearchBookableAccommodationsState {
  loadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  loadingAccommodationPublicDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  results: SearchResultItem[];
  accommodationPublicDataResult: AccommodationPublicDetails;
  searchFilter: SearchBookableAccommodationsFilter;
  pagination: PaginationResponse;
}

const initialState: SearchBookableAccommodationsState = {
  loadingStatus: LOADING_STATUS.IDLE,
  loadingAccommodationPublicDataStatus: LOADING_STATUS.IDLE,
  results: [],
  accommodationPublicDataResult: null,
  searchFilter: {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    cultureOption: LOCALES.Fi,
    orderBy: ORDER_BY.Random,
    orderDirection: ORDER_DIRECTION.Asc,
    includeBlockedByAvailabilityRules: true,
    autosuggestions: [],
    arrivalDateTime: undefined,
    lengthOfStay: undefined,
    qualityRatings: [],
    interhomeService: undefined,
    searchString: '',
    accommodationId: undefined,
    nearbySearchRadius: 0,
    nearbySearchLimit: 1000000,
    nearbyAccommodationsCount: 1000000,
    minimumNumericAttributes: {},
    maximumNumericAttributes: {},
    booleanAttributes: [],
    multiSelectAttributes: [],
    textAttributes: {},
    logPriceCalculation: false,
    skipAvailabilityCheck: false,
  },
  pagination: InitialPaginationResponse,
};

export const searchBookableAccommodations = createAsyncThunk(
  'search/searchBookableAccommodations',
  async (filter: SearchBookableAccommodationsFilter, { rejectWithValue }) => {
    try {
      return await searchService.searchAccommodations(filter);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccommodationPublicData = createAsyncThunk(
  'search/getAccommodationPublicData',
  async (params: AccommodationPublicDetailsParams, { rejectWithValue }) => {
    try {
      return await searchService.getAccommodationPublicDetails(params);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchDefaultRateDetailsAsync = createAsyncThunk(
  'pricing/fetchDefaultRateDetails',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchDefaultRateDetails(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.result);
    }
  },
);

export const clearSearchResults = createAsyncThunk(
  'search/clearSearchResults',
  async (_, { dispatch }) => {
    const response = await resetSearchResults();
    return response;
  },
);

const searchSlice = createSlice({
  name: 'searchBookableAccommodations',
  initialState,
  reducers: {
    setSearchFilters(
      state,
      action: PayloadAction<SearchBookableAccommodationsFilter>,
    ) {
      state.searchFilter = action.payload;
    },
    resetFilters(state) {
      state.searchFilter = initialState.searchFilter;
      state.results = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchBookableAccommodations.pending, state => {
        state.loadingStatus = LOADING_STATUS.LOADING;
        state.results = [];
      })
      .addCase(searchBookableAccommodations.fulfilled, (state, action) => {
        const result = action.payload as { data: SearchResultItem[] }; // Type assertion
        state.loadingStatus = LOADING_STATUS.IDLE;
        state.results = result?.data || []; // transformSearchResults(result?.data || []);
        // state.results = transformSearchResults(action.payload.data);
        state.pagination = PopulatePagination(action.payload);
      })
      .addCase(searchBookableAccommodations.rejected, (state, action) => {
        state.loadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getAccommodationPublicData.pending, state => {
        state.loadingAccommodationPublicDataStatus = LOADING_STATUS.LOADING;
        state.accommodationPublicDataResult = null;
      })
      .addCase(getAccommodationPublicData.fulfilled, (state, action) => {
        const { result, isSuccess, message } = action.payload;
        if (isSuccess && result) {
          // `result` is typed as AccommodationPublicDetails
          state.accommodationPublicDataResult = result;
        }

        state.loadingAccommodationPublicDataStatus = LOADING_STATUS.IDLE;
        // state.accommodationPublicDataResult = result || null; // transformSearchResults(result?.data || []);
        // state.results = transformSearchResults(action.payload.data);
        // state.pagination = PopulatePagination(action.payload);
      })
      .addCase(getAccommodationPublicData.rejected, (state, action) => {
        state.loadingAccommodationPublicDataStatus = LOADING_STATUS.FAILED;
      })
      .addCase(clearSearchResults.fulfilled, state => {
        state.results = [];
      });
  },
});

export const selectLoadingStatus = (state: RootState) =>
  state.searchBookableAccommodations.loadingStatus;
export const selectSearchResults = (state: RootState) =>
  state.searchBookableAccommodations.results;
export const selectSearchFilter = (state: RootState) =>
  state.searchBookableAccommodations.searchFilter;
export const selectBookableAccommodationsPagination = (state: RootState) =>
  state.searchBookableAccommodations.pagination;
export const selectAccommodationPublicDataLoadingStatus = (state: RootState) =>
  state.searchBookableAccommodations.loadingAccommodationPublicDataStatus;
export const selectAccommodationPublicDataResult = (state: RootState) =>
  state.searchBookableAccommodations.accommodationPublicDataResult;

export const { setSearchFilters, resetFilters } = searchSlice.actions;

export default searchSlice.reducer;
